/* eslint-disable camelcase */
import { BlackboxValues } from 'modules/fraud-force/fraudForce';
import { useCallback, useEffect, useRef } from 'react';
import { mainWebsiteOrigin } from 'utils';

export const useBlackBoxRequestFromParentWindow = () => {
    const blackBoxRequstCounter = useRef(0);
    const pendingBlackBoxRequests = useRef(new Map());

    // Set up the event listener for the bb response and resolve the promise
    useEffect(() => {
        const handleMessage = (event: any) => {
            // Validate the origin to ensure only messages from the expected parent are processed
            if (event.origin !== mainWebsiteOrigin()) return;

            const { requestId, response, type } = event.data;
            if (type === 'blackBoxResponse' && pendingBlackBoxRequests.current.has(requestId)) {
                // resolve the promise prior to removing the queued request
                pendingBlackBoxRequests.current.get(requestId)(response);
                pendingBlackBoxRequests.current.delete(requestId);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    // wrap the async postMessage to the parent as a promise
    const sendBlackBoxRequestToParentWindow = useCallback(() => {
        return new Promise<BlackboxValues>((resolve) => {
            const requestId = ++blackBoxRequstCounter.current;
            let timeout: any; // Declare timeout variable so we can reference it later

            // Wrap the resolve function to clear the timeout if a response is received in time.
            const resolveWithClear = (response: BlackboxValues) => {
                clearTimeout(timeout); // Clear the timeout before resolving the promise
                resolve(response);
            };

            // Store the resolver function in the pendingRequests map
            pendingBlackBoxRequests.current.set(requestId, resolveWithClear);

            // Send the message to the parent window using postMessage
            window.parent.postMessage({ type: 'blackBoxRequest', requestId }, mainWebsiteOrigin());

            // Set up a timeout to reject the promise if no response is received within 8 seconds.
            timeout = setTimeout(() => {
                if (pendingBlackBoxRequests.current.has(requestId)) {
                    //resolve with no bb captured
                    pendingBlackBoxRequests.current.get(requestId)({
                        blackBoxValue: null,
                        secondaryBlackBoxValue: null,
                    });
                    pendingBlackBoxRequests.current.delete(requestId);
                }
            }, 8000);
        });
    }, []);

    return { sendBlackBoxRequestToParentWindow };
};
